import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Input, MessageBarBody, MessageBarTitle, Option, Switch, Tag, TagGroup } from '@fluentui/react-components';
import { LinkDismiss20Regular, DoorTag20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Consultorio, QueueDisplayScreen, Sector, Sede } from '../../../../models/Entities/Turnero';
import { ErrorType } from '../../../../models/HttpError';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../../theme/components/GuideField';
import { useOfficesFormStyles } from '../Offices.Styles';
import { useDisplayPreviewStyles } from './DisplayDrawer.Styles';
import {
  DisplayFormValues,
  applyDisplayLocation,
  buildDisplayPayload,
  displayToFormValues,
  findById,
  getSectorRooms,
  getSectoresBySede,
  isDisplayPaired,
  validateDisplayForm,
} from '../Offices.Functions';
import { DisplayPairingPill, ErrorBanner } from '../OfficesCommon/OfficesCommon.Component';

export interface DisplayDrawerProps {
  open: boolean;
  display?: QueueDisplayScreen;
  defaults?: { sedeId?: number | null; sectorId?: number | null };
  sedes: Sede[];
  sectores: Sector[];
  consultorios: Consultorio[];
  displays: QueueDisplayScreen[];
  isSaving: boolean;
  saveError?: ErrorType;
  onSubmit: (payload: QueueDisplayScreen, id?: number) => void;
  onEditSectorRooms?: (sectorId: number) => void;
  onClose: () => void;
}

const NONE = 'none';

/** Vista previa de lo que muestra la TV: los llamados de los consultorios del sector elegido. */
export const DisplayPreview = ({
  values,
  sector,
  sede,
  rooms,
}: {
  values: DisplayFormValues;
  sector?: Sector;
  sede?: Sede;
  rooms: Consultorio[];
}) => {
  const { t } = useTranslation();
  const styles = useDisplayPreviewStyles();
  return (
    <div className={styles.wrapper} data-testid="display-preview">
      <div className={styles.screen} aria-label={t('Offices.display.previewTitle')} role="img">
        <div className={styles.screenHeader}>
          <span className={styles.screenTitle}>{values.name.trim() || t('Offices.display.previewUnnamed')}</span>
          <span className={styles.screenSubtitle}>
            {[sector?.name, sede?.name].filter(Boolean).join(' · ') || t('Offices.display.previewNoSector')}
          </span>
        </div>
        <div className={styles.current}>
          <span className={styles.currentLabel}>{t('Offices.display.previewCurrentCall')}</span>
          <span className={styles.currentPatient}>{t('Offices.display.previewPatient')}</span>
          <span className={styles.currentRoom}>{rooms[0]?.name || t('Offices.display.previewRoom')}</span>
        </div>
        {rooms.length > 0 ? (
          <div className={styles.rooms}>
            {rooms.map((room) => (
              <span key={room.id} className={styles.room}>
                {room.name}
              </span>
            ))}
          </div>
        ) : (
          <span className={styles.empty}>{t('Offices.display.previewNoRooms')}</span>
        )}
        {!values.isActive && (
          <div className={styles.overlay}>
            <span className={styles.overlayLabel}>{t('Offices.display.previewInactive')}</span>
          </div>
        )}
      </div>
      <span className={styles.caption}>{t('Offices.display.previewHint')}</span>
    </div>
  );
};

const DisplayDrawer = ({
  open,
  display,
  defaults,
  sedes,
  sectores,
  consultorios,
  displays,
  isSaving,
  saveError,
  onSubmit,
  onEditSectorRooms,
  onClose,
}: DisplayDrawerProps) => {
  const { t } = useTranslation();
  const styles = useOfficesFormStyles();
  const editing = display?.id !== undefined;
  const [values, setValues] = useState<DisplayFormValues>(displayToFormValues(display, defaults));
  const [showErrors, setShowErrors] = useState(false);

  // B11: hidratar sólo al abrir o al cambiar de display. Un GET de la lista mientras el drawer está
  // abierto (polling del registro, refresco) no debe borrar lo que el usuario está editando.
  useEffect(() => {
    if (open) {
      setValues(displayToFormValues(display, defaults));
      setShowErrors(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, display?.id]);

  const errors = validateDisplayForm(values, displays, sectores, display?.id);
  const sede = findById(sedes, values.sedeId);
  const sector = findById(sectores, values.sectorId);
  const sectorOptions = useMemo(() => getSectoresBySede(sectores, values.sedeId), [sectores, values.sedeId]);
  const rooms = useMemo(() => getSectorRooms(sector, consultorios), [sector, consultorios]);
  const wasPaired = isDisplayPaired(display);
  const unpairing = wasPaired && !values.fingerprint;

  const submit = (event?: React.FormEvent) => {
    event?.preventDefault();
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    onSubmit(buildDisplayPayload(values, display?.id), display?.id);
  };

  const fieldError = (key: keyof typeof errors) => ({
    error: showErrors && errors[key] ? t(errors[key] as string) : undefined,
  });

  return (
    <GuideDrawer
      open={open}
      size="large"
      title={editing ? t('Offices.display.editTitle') : t('Offices.display.createTitle')}
      onDismiss={onClose}
      preventDismiss={isSaving}
      closeLabel={t('Offices.actions.close')}
      testId="display-drawer"
      secondaryAction={{ label: t('Offices.actions.cancel') }}
      primaryAction={{
        label: isSaving ? t('Offices.actions.saving') : editing ? t('Offices.actions.update') : t('Offices.actions.create'),
        onClick: () => submit(),
        loading: isSaving,
      }}
    >
      <div className={styles.twoColumns}>
        <GuideDrawerForm onSubmit={submit} testId="display-form">
          {saveError && <ErrorBanner title={t('Offices.messages.saveError')} error={saveError} testId="display-save-error" />}
          <GuideField label={t('Offices.display.name')} required {...fieldError('name')}>
            <Input
              value={values.name}
              maxLength={100}
              placeholder={t('Offices.display.namePlaceholder')}
              onChange={(_, d) => setValues((v) => ({ ...v, name: d.value }))}
            />
          </GuideField>
          <GuideField label={t('Offices.display.description')}>
            <Input
              value={values.description}
              maxLength={250}
              placeholder={t('Offices.display.descriptionPlaceholder')}
              onChange={(_, d) => setValues((v) => ({ ...v, description: d.value }))}
            />
          </GuideField>
          <GuideField
            label={t('Offices.display.registrationNumber')}
            required
            hint={t('Offices.display.registrationNumberHint')}
            {...fieldError('registrationNumber')}
          >
            <Input
              value={values.registrationNumber}
              maxLength={50}
              placeholder="9999-9999"
              onChange={(_, d) => setValues((v) => ({ ...v, registrationNumber: d.value }))}
            />
          </GuideField>
          <GuideFieldGrid data-testid="display-location">
            <GuideField label={t('Offices.display.sede')}>
              <Dropdown
                value={sede?.name || t('Offices.display.noSede')}
                selectedOptions={[values.sedeId !== undefined && values.sedeId !== null ? String(values.sedeId) : NONE]}
                onOptionSelect={(_, d) =>
                  setValues((v) =>
                    applyDisplayLocation(v, sectores, { sedeId: !d.optionValue || d.optionValue === NONE ? null : Number(d.optionValue) })
                  )
                }
                data-testid="display-sede"
              >
                <Option value={NONE} text={String(t('Offices.display.noSede'))}>
                  {t('Offices.display.noSede')}
                </Option>
                {sedes.map((s) => (
                  <Option key={s.id} value={String(s.id)} text={s.name || ''}>
                    {s.name}
                  </Option>
                ))}
              </Dropdown>
            </GuideField>
            <GuideField label={t('Offices.display.sector')} hint={t('Offices.display.sectorHint')} {...fieldError('sectorId')}>
              <Dropdown
                value={sector?.name || t('Offices.display.noSector')}
                selectedOptions={[values.sectorId !== undefined && values.sectorId !== null ? String(values.sectorId) : NONE]}
                onOptionSelect={(_, d) =>
                  setValues((v) =>
                    applyDisplayLocation(v, sectores, { sectorId: !d.optionValue || d.optionValue === NONE ? null : Number(d.optionValue) })
                  )
                }
                data-testid="display-sector"
              >
                <Option value={NONE} text={String(t('Offices.display.noSector'))}>
                  {t('Offices.display.noSector')}
                </Option>
                {sectorOptions.map((s) => (
                  <Option key={s.id} value={String(s.id)} text={s.name || ''}>
                    {s.name}
                  </Option>
                ))}
              </Dropdown>
            </GuideField>
          </GuideFieldGrid>

          <GuideDrawerSection title={t('Offices.display.roomsTitle')} description={t('Offices.display.roomsHint')}>
            {sector ? (
              <>
                {rooms.length > 0 ? (
                  <TagGroup aria-label={t('Offices.display.roomsTitle')} data-testid="display-rooms">
                    {rooms.map((room) => (
                      <Tag key={room.id} value={String(room.id)} icon={<DoorTag20Regular />}>
                        {room.name}
                      </Tag>
                    ))}
                  </TagGroup>
                ) : (
                  <span className={styles.muted}>{t('Offices.display.sectorWithoutRooms')}</span>
                )}
                {onEditSectorRooms && sector.id !== undefined && (
                  <div>
                    <Button appearance="secondary" onClick={() => onEditSectorRooms(sector.id as number)} disabled={isSaving}>
                      {t('Offices.display.manageRooms')}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <span className={styles.muted}>{t('Offices.display.selectSectorForRooms')}</span>
            )}
          </GuideDrawerSection>

          <GuideField label={t('Offices.display.active')}>
            <Switch
              checked={values.isActive}
              label={values.isActive ? t('Offices.common.yes') : t('Offices.common.no')}
              onChange={(_, d) => setValues((v) => ({ ...v, isActive: d.checked }))}
            />
          </GuideField>

          {editing && display && (
            <GuideField label={t('Offices.display.pairing')} hint={t('Offices.display.pairingHint')}>
              <div className={styles.readOnlyBox} data-testid="display-pairing">
                <DisplayPairingPill display={{ ...display, fingerprint: values.fingerprint, fingerPrint: values.fingerprint }} />
                {wasPaired && values.fingerprint && (
                  <Button
                    appearance="subtle"
                    icon={<LinkDismiss20Regular />}
                    onClick={() => setValues((v) => ({ ...v, fingerprint: '' }))}
                  >
                    {t('Offices.display.unpair')}
                  </Button>
                )}
                {unpairing && (
                  <Button appearance="subtle" onClick={() => setValues((v) => ({ ...v, fingerprint: displayToFormValues(display).fingerprint }))}>
                    {t('Offices.display.undoUnpair')}
                  </Button>
                )}
              </div>
            </GuideField>
          )}
          {unpairing && (
            <GuideMessageBar intent="warning">
              <MessageBarBody>
                <MessageBarTitle>{t('Offices.display.unpairWarningTitle')}</MessageBarTitle>
                {t('Offices.display.unpairWarning')}
              </MessageBarBody>
            </GuideMessageBar>
          )}
        </GuideDrawerForm>
        <GuideDrawerSection title={t('Offices.display.previewTitle')}>
          <DisplayPreview values={values} sector={sector} sede={sede} rooms={rooms} />
        </GuideDrawerSection>
      </div>
    </GuideDrawer>
  );
};

export default DisplayDrawer;
